/**
 * Validate if a string is a valid URL
 */
export function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Extract domain from URL
 * @example extractDomain('https://www.example.com/article') => 'example.com'
 */
export function extractDomain(url: string): string | null {
  try {
    const parsed = new URL(url);
    // Remove 'www.' prefix if present
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Generate favicon URL from domain
 * Using Google's favicon service
 */
export function getFaviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
}

/**
 * Calculate reading time in minutes based on word count
 * Average reading speed: 200 words per minute
 */
export function calculateReadingTime(wordCount: number): number {
  const wordsPerMinute = 200;
  return Math.ceil(wordCount / wordsPerMinute);
}

/**
 * Count words in text
 */
export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Truncate text to a maximum length with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Format date to ISO date string (YYYY-MM-DD)
 */
export function formatDateISO(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Parse ISO date string to Date
 */
export function parseISODate(dateStr: string): Date {
  return new Date(dateStr);
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Format reading time for display
 * @example formatReadingTime(5) => '5 min read'
 */
export function formatReadingTime(minutes: number): string {
  if (minutes < 1) return '< 1 min read';
  return `${minutes} min read`;
}

/**
 * Sanitize filename for safe storage
 */
export function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-z0-9.-]/gi, '_')
    .replace(/_{2,}/g, '_')
    .toLowerCase();
}

/**
 * Generate a cursor for pagination from a timestamp and ID.
 * Uses btoa() instead of Buffer for cross-platform compatibility (Node, browser, React Native).
 */
export function encodeCursor(timestamp: string, id: number): string {
  return btoa(JSON.stringify({ t: timestamp, i: id }));
}

/**
 * Decode a cursor for pagination.
 * Uses atob() instead of Buffer for cross-platform compatibility (Node, browser, React Native).
 */
export function decodeCursor(cursor: string): { timestamp: string; id: number } | null {
  try {
    const parsed = JSON.parse(atob(cursor));
    if (typeof parsed.t !== 'string' || typeof parsed.i !== 'number') {
      return null;
    }
    if (isNaN(Date.parse(parsed.t))) {
      return null;
    }
    return { timestamp: parsed.t, id: parsed.i };
  } catch {
    return null;
  }
}
